import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Zap, Droplets, Clock, MapPin, User } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons
const createCustomIcon = (type) => {
  const color = type === 'electricity' ? '#FF8C00' : '#3B82F6';
  const icon = type === 'electricity' ? '⚡' : '💧';
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 36px;
        height: 36px;
        background: ${color};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.4);
        border: 2px solid white;
      ">
        ${icon}
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -20]
  });
};

// Component to set map view
const MapViewController = ({ center, zoom }) => {
  const map = useMap();
  React.useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
};

export const OutageMap = ({ outages = [], className = '' }) => {
  // Default center: Lubumbashi, DRC
  const defaultCenter = [-11.6600, 27.4794];
  const defaultZoom = 13;

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "d MMM 'à' HH:mm", { locale: fr });
    } catch {
      return dateString;
    }
  };

  const getSeverityLabel = (severity) => {
    const labels = {
      low: 'Faible',
      medium: 'Moyen',
      high: 'Élevé'
    };
    return labels[severity] || severity;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: 'text-green-500',
      medium: 'text-yellow-500',
      high: 'text-red-500'
    };
    return colors[severity] || 'text-gray-500';
  };

  return (
    <div className={`${className}`} style={{ height: '100%', width: '100%' }} data-testid="outage-map">
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%', borderRadius: '0.75rem' }}
        scrollWheelZoom={true}
      >
        <MapViewController center={defaultCenter} zoom={defaultZoom} />
        
        {/* Dark themed map tiles */}
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        
        {outages.map((outage) => (
          <Marker
            key={outage.id}
            position={[outage.latitude, outage.longitude]}
            icon={createCustomIcon(outage.type)}
          >
            <Popup>
              <div className="min-w-[200px] p-2 space-y-3">
                {/* Header */}
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${outage.type === 'electricity' ? 'bg-orange-500' : 'bg-blue-500'}`}>
                    {outage.type === 'electricity' ? (
                      <Zap className="h-4 w-4 text-black" />
                    ) : (
                      <Droplets className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <div>
                    <p className="font-bold text-foreground">
                      {outage.type === 'electricity' ? 'Électricité (SNEL)' : 'Eau (REGIDESO)'}
                    </p>
                    <p className={`text-xs font-medium ${getSeverityColor(outage.severity)}`}>
                      Gravité: {getSeverityLabel(outage.severity)}
                    </p>
                  </div>
                </div>
                
                {/* Location */}
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <span className="text-foreground">{outage.location_name}</span>
                </div>
                
                {/* Description */}
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {outage.description}
                </p>
                
                {/* Footer */}
                <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-2">
                  <div className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    <span>{outage.reporter_name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>{formatDate(outage.created_at)}</span>
                  </div>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};
